import type { Zone } from "../types";
import { getActiveTpid, getFeatureTypeName } from "./timetablePeriods";
import { API_ENDPOINTS } from "./apiEndpoints";

export type MunicipalityWithLines = {
  Key: string;
  Value: Array<{
    LineNrReal: number;
    LineName: string;
    LineType?: {
      Key: string;
      Label: string;
      ParentGroupKey?: string | null;
      OgtTrafficTypeGroupCssClass?: string | null;
    };
    Ogt?: { OgtLineNamePreformatted?: string | null };
  }>;
};

type LineMeta = {
  displayName: string;
  municipalities: string[];
  urlSegment?: string | null;
};

const lineNamesByTpid = new Map<string, Map<number, string>>();
const lineNamesInflightByTpid = new Map<string, Promise<Map<number, string>>>();

const lineMetaByTpid = new Map<string, Map<number, LineMeta>>();
const lineMetaInflightByTpid = new Map<
  string,
  Promise<Map<number, LineMeta>>
>();

export async function ensureLineNamesMap(): Promise<{
  tpid: string;
  map: Map<number, string>;
}> {
  const tpidVal = await getActiveTpid();
  const tpid = String(tpidVal ?? "");

  const cached = lineNamesByTpid.get(tpid);
  if (cached) return { tpid, map: cached };

  const inflight = lineNamesInflightByTpid.get(tpid);
  if (inflight) {
    const map = await inflight;
    return { tpid, map };
  }

  const promise = (async () => {
    const muni = await fetchMunicipalitiesWithLines();
    const map = new Map<number, string>();

    for (const m of muni) {
      for (const v of m.Value) {
        const pre =
          v.Ogt?.OgtLineNamePreformatted?.trim() ?? `Linje ${v.LineNrReal}`;
        map.set(v.LineNrReal, pre);
      }
    }

    lineNamesByTpid.set(tpid, map);
    lineNamesInflightByTpid.delete(tpid);
    return map;
  })();

  lineNamesInflightByTpid.set(tpid, promise);
  const map = await promise;
  return { tpid, map };
}

export async function ensureLineMetaMap(): Promise<{
  tpid: string;
  map: Map<number, LineMeta>;
}> {
  const tpidVal = await getActiveTpid();
  const tpid = String(tpidVal ?? "");

  const cached = lineMetaByTpid.get(tpid);
  if (cached) return { tpid, map: cached };

  const inflight = lineMetaInflightByTpid.get(tpid);
  if (inflight) {
    const map = await inflight;
    return { tpid, map };
  }

  const promise = (async () => {
    const muni = await fetchMunicipalitiesWithLines();
    const map = new Map<number, LineMeta>();

    for (const m of muni) {
      for (const v of m.Value) {
        const key = v.LineNrReal;
        const prev = map.get(key);
        const names =
          (v as any)?.Ogt?.OgtMunicipalities?.map(
            (x: any) => x.MunicipalityName
          ) ?? [];
        const uniqueMunicipalities = prev
          ? Array.from(new Set([...(prev.municipalities ?? []), ...names]))
          : Array.from(new Set(names));

        map.set(key, {
          displayName:
            (v as any)?.Ogt?.OgtLineNamePreformatted ?? `Linje ${key}`,
          municipalities: uniqueMunicipalities,
          urlSegment: (v as any)?.Ogt?.OgtLineUrlSegment ?? null,
        });
      }
    }

    lineMetaByTpid.set(tpid, map);
    lineMetaInflightByTpid.delete(tpid);
    return map;
  })();

  lineMetaInflightByTpid.set(tpid, promise);
  const map = await promise;
  return { tpid, map };
}

export async function getMunicipalitiesForLine(
  lineNrReal: number
): Promise<string[]> {
  const { map } = await ensureLineMetaMap();
  return map.get(lineNrReal)?.municipalities ?? [];
}

export async function getLineMeta(
  lineNrReal: number
): Promise<LineMeta | undefined> {
  const { map } = await ensureLineMetaMap();
  return map.get(lineNrReal);
}

export async function fetchMunicipalitiesWithLinesByTrafficType(
  trafficTypeKey: string
): Promise<MunicipalityWithLines[]> {
  const tpid = await getActiveTpid().catch(() => null);
  const url = API_ENDPOINTS.LINES_GET_BY_TRAFFIC_TYPE(
    trafficTypeKey,
    tpid != null ? String(tpid) : undefined
  );

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error("API (GetByTrafficType) fel: " + res.status);
  }

  return res.json();
}

export async function fetchMunicipalitiesWithLines(): Promise<
  MunicipalityWithLines[]
> {
  return fetchMunicipalitiesWithLinesByTrafficType("NÄRTRAFIK");
}

// build WFS GetFeature XML for zones
function buildWfsZoneRequestXml(
  featureType: string,
  tpid: string,
  lineIds: number[]
): string {
  const propertyNames = ["linje", "geom"]
    .map((p) => `<wfs:PropertyName>${p}</wfs:PropertyName>`)
    .join("");

  const orLines = lineIds
    .map(
      (id) => `
        <ogc:PropertyIsEqualTo>
          <ogc:PropertyName>linje</ogc:PropertyName>
          <ogc:Literal>${id}</ogc:Literal>
        </ogc:PropertyIsEqualTo>`
    )
    .join("");

  return `
<GetFeature service="WFS" version="1.0.0" outputFormat="application/json"
  xmlns:wfs="http://www.opengis.net/wfs"
  xmlns:ogc="http://www.opengis.net/ogc"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.opengis.net/wfs http://schemas.opengis.net/wfs/1.0.0/WFS-basic.xsd">
  <wfs:Query typeName="${featureType}">
    ${propertyNames}
    <ogc:Filter>
      <ogc:And>
        <ogc:PropertyIsEqualTo>
          <ogc:PropertyName>tpid</ogc:PropertyName>
          <ogc:Literal>${tpid}</ogc:Literal>
        </ogc:PropertyIsEqualTo>
        <ogc:Or>
          ${orLines}
        </ogc:Or>
      </ogc:And>
    </ogc:Filter>
  </wfs:Query>
</GetFeature>`.trim();
}

// zone cache
const zonesCache = new Map<string, Zone[]>();
const zonesInflight = new Map<string, Promise<Zone[]>>();

function toPolygonFromGeometry(geom: any): Array<{ lat: number; lng: number }> {
  if (!geom) return [];
  if (geom.type === "Polygon") {
    return (
      (geom.coordinates?.[0] as [number, number][])?.map(([lng, lat]) => ({
        lat,
        lng,
      })) ?? []
    );
  }
  if (geom.type === "MultiPolygon") {
    const firstPoly = geom.coordinates?.[0]?.[0] as
      | [number, number][]
      | undefined;
    return firstPoly?.map(([lng, lat]) => ({ lat, lng })) ?? [];
  }
  if (geom.type === "LineString") {
    return (
      (geom.coordinates as [number, number][])?.map(([lng, lat]) => ({
        lat,
        lng,
      })) ?? []
    );
  }
  return [];
}

function formatZoneNameOnly(
  lineNamesMap: Map<number, string> | undefined,
  linje: unknown
): string {
  const num = Number(linje);
  const disp = !Number.isNaN(num) ? lineNamesMap?.get(num) : undefined;
  return disp && /\S/.test(disp) ? disp : `Linje ${linje ?? "unknown"}`;
}

// get zones for specific lines
export async function fetchZonesForLines(lineIds: number[]): Promise<Zone[]> {
  if (!lineIds.length) return [];

  const tpid = await getActiveTpid();
  if (tpid == null) throw new Error("Ingen aktiv tidtabellsperiod hittades");

  const featureType = await getFeatureTypeName("Lines").catch(
    () => "ogt:linjer3"
  );

  const cacheKey = `${tpid}|${[...lineIds].sort((a, b) => a - b).join(",")}`;

  const cached = zonesCache.get(cacheKey);
  if (cached) return cached;

  const inflight = zonesInflight.get(cacheKey);
  if (inflight) return await inflight;

  const promise = (async () => {
    let lineNamesMap: Map<number, string> | undefined;
    try {
      ({ map: lineNamesMap } = await ensureLineNamesMap());
    } catch {
      lineNamesMap = undefined; // fallback
    }

    const xml = buildWfsZoneRequestXml(
      String(featureType),
      String(tpid),
      lineIds
    );

    const res = await fetch(API_ENDPOINTS.GEO_FETCH, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
      body: `=${encodeURIComponent(xml)}`,
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("Geo/fetch (zoner) fel:", res.status, text);
      throw new Error("Geo/fetch (zoner) fel: " + res.status);
    }

    const geoJson = await res.json();
    const features: any[] = geoJson.features || [];

    const zones: Zone[] = features.map((f, idx) => {
      const props = f.properties || {};
      const geom = f.geometry || {};
      const polygon = toPolygonFromGeometry(geom);
      const linje = props.linje ?? "unknown";
      return {
        id: `${linje}_${idx}`,
        name: formatZoneNameOnly(lineNamesMap, linje),
        polygon,
      };
    });

    zonesCache.set(cacheKey, zones);
    zonesInflight.delete(cacheKey);
    return zones;
  })();

  zonesInflight.set(cacheKey, promise);
  return await promise;
}

// get zone by its id
export async function fetchZoneById(id: string): Promise<Zone> {
  const [linjeStr, idxStr] = id.split("_");
  const linje = parseInt(linjeStr, 10);
  const idx = parseInt(idxStr, 10);

  if (Number.isNaN(linje) || Number.isNaN(idx)) {
    throw new Error(`Zone-id ogiltigt: ${id}`);
  }

  const zones = await fetchZonesForLines([linje]);
  if (idx < 0 || idx >= zones.length) {
    throw new Error(`Zone med id=${id} hittades inte (index ${idx} utanför)`);
  }
  return zones[idx];
}

// Cache clearing helpers
export function clearZonesCaches() {
  zonesCache.clear();
  zonesInflight.clear();
}
export function clearLineNamesCache(tpid?: string) {
  if (tpid) {
    lineNamesByTpid.delete(tpid);
    lineNamesInflightByTpid.delete(tpid);
  } else {
    lineNamesByTpid.clear();
    lineNamesInflightByTpid.clear();
  }
}
export function clearLineMetaCache(tpid?: string) {
  if (tpid) {
    lineMetaByTpid.delete(tpid);
    lineMetaInflightByTpid.delete(tpid);
  } else {
    lineMetaByTpid.clear();
    lineMetaInflightByTpid.clear();
  }
}
